import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import {
  LayoutDashboard,
  FileText,
  Camera,
  Building2,
  Settings,
  LogOut,
  Menu,
  X,
  Loader2,
  User,
  Copy,
  BarChart3,
} from 'lucide-react';
import { login as apiLogin, logout as apiLogout, isAuthenticated, getUsername } from './services/api';
import Dashboard from './pages/Dashboard';
import FacturasList from './pages/FacturasList';
import FacturaDetail from './pages/FacturaDetail';
import UploadPage from './pages/Upload';
import Proveedores from './pages/Proveedores';
import SettingsPage from './pages/Settings';
import Duplicados from './pages/Duplicados';
import ResumenMensual from './pages/ResumenMensual';
import toast from 'react-hot-toast';

const NAV_ITEMS = [
  { to: '/', icon: LayoutDashboard, label: 'Inicio' },
  { to: '/facturas', icon: FileText, label: 'Facturas' },
  { to: '/duplicados', icon: Copy, label: 'Duplicados' },
  { to: '/resumen', icon: BarChart3, label: 'Resumen' },
  { to: '/upload', icon: Camera, label: 'Escanear' },
  { to: '/proveedores', icon: Building2, label: 'Proveedores' },
  { to: '/settings', icon: Settings, label: 'Ajustes' },
];

function Logo({ className = '', size = 'md' }) {
  const sizeClasses = {
    sm: 'h-7',
    md: 'h-8',
    lg: 'h-12',
    xl: 'h-20',
  };
  return (
    <img
      src="/logo.png"
      alt="FactuScan"
      className={`${sizeClasses[size]} w-auto object-contain ${className}`}
    />
  );
}

function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setLoading(true);
    try {
      await apiLogin(username, password);
      onLogin();
    } catch (err) {
      toast.error(err.message || 'Error de autenticacion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-800 to-primary-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-white rounded-2xl p-4 shadow-lg">
              <Logo size="xl" />
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Usuario</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="input-field"
              placeholder="Tu usuario"
              autoComplete="username"
              autoFocus
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contrasena</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field"
              placeholder="Tu contrasena"
              autoComplete="current-password"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? 'Iniciando sesion...' : 'Iniciar Sesion'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Sidebar({ mobileOpen, onClose }) {
  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 lg:translate-x-0 lg:static lg:z-auto hidden lg:block ${
          mobileOpen ? 'translate-x-0 !block' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          <Logo size="md" />
          <button onClick={onClose} className="lg:hidden text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-4 space-y-1">
          {NAV_ITEMS.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`
              }
            >
              <Icon className="w-5 h-5" />
              {label}
            </NavLink>
          ))}
        </nav>
      </aside>
    </>
  );
}

function BottomNav() {
  const location = useLocation();
  if (location.pathname.match(/^\/facturas\/\d+/)) return null;

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 lg:hidden safe-area-bottom">
      <div className="flex items-center justify-around h-16">
        {NAV_ITEMS.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 flex-1 h-full text-[10px] font-medium transition-colors ${
                isActive
                  ? 'text-primary-600'
                  : 'text-gray-400 active:text-gray-600'
              }`
            }
          >
            <Icon className="w-5 h-5" />
            <span>{label}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const username = getUsername();

  const handleLogout = () => {
    apiLogout();
    window.location.reload();
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-14 lg:h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6 shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden text-gray-600 hover:text-gray-900 -ml-1"
            >
              <Menu className="w-6 h-6" />
            </button>
            <Logo size="sm" className="lg:hidden" />
          </div>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <User className="w-4 h-4" />
              <span className="hidden sm:inline">{username}</span>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-3 pb-20 lg:p-6 lg:pb-6">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/facturas" element={<FacturasList />} />
            <Route path="/facturas/:id" element={<FacturaDetail />} />
            <Route path="/duplicados" element={<Duplicados />} />
            <Route path="/resumen" element={<ResumenMensual />} />
            <Route path="/upload" element={<UploadPage />} />
            <Route path="/proveedores" element={<Proveedores />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        <BottomNav />
      </div>
    </div>
  );
}

export default function App() {
  const [authenticated, setAuthenticated] = useState(isAuthenticated());

  useEffect(() => {
    const check = () => setAuthenticated(isAuthenticated());
    window.addEventListener('storage', check);
    return () => window.removeEventListener('storage', check);
  }, []);

  return (
    <BrowserRouter>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            fontSize: '14px',
            maxWidth: '90vw',
          },
        }}
      />
      {authenticated ? (
        <AppLayout />
      ) : (
        <LoginScreen onLogin={() => setAuthenticated(true)} />
      )}
    </BrowserRouter>
  );
}
